// @flow

import React, { Fragment } from 'react';

import { Link } from '@zapier/design-system';

import errorChild from 'app/get-help/content/nodes/loggedIn/zapTroubleshooting/error';
import help from 'app/get-help/components/blocks/Help';
import missingChild from 'app/get-help/content/nodes/loggedIn/zapTroubleshooting/missing';
import radio from 'app/get-help/components/blocks/Radio';
import zap from 'app/get-help/components/blocks/Zap';
import { createDefaultContact } from 'app/get-help/content/nodes/loggedIn/zapTroubleshooting/common';
import { ZENDESK_CUSTOM_FIELD_IDS } from 'app/get-help/constants';

const triggerHelp = `If there haven't been any items from the last 7 days, create a new record that has the field filled out and test your Zap. Then, provide the record you created as a screenshot by linking to or uploading it below. That way we can take a better look at where the problem might be occurring.`;

const troubleshooting = zap({
  title: 'Which Zap isn‘t working?',
  instructions:
    'By selecting a Zap, we‘ll be able to get back to you with a better and faster response.',
  kind: ['workflow'],
  kindDisplayName: 'Zap',
  child: radio({
    id: 'problem',
    label: 'Tell us more about your problem:',
    zendeskCustomFieldId: ZENDESK_CUSTOM_FIELD_IDS.TROUBLESHOOTING_TELL_US_MORE,
    items: [
      {
        value: 'error',
        label: `I'm getting an error message`,
        zendeskCustomFieldValue: 'cf-error-message',
        child: errorChild,
      },
      {
        value: 'trigger',
        label: `My Zap isn't triggering`,
        zendeskCustomFieldValue: 'cf-zap-not-triggering',
        child: help({
          docs: {
            title: 'Check if the Zap is set up correctly',
            body: () => (
              <p>
                Sometimes Zaps don‘t trigger properly because no new records
                were made after the Zap was turned on, there‘s a problem with
                the account connection, or there‘s information missing on parts
                of the Zap. Luckily, this is something we can typically resolve!
                For information on how to identify the problem and fix the
                issue,{' '}
                <Link href="/help/troubleshoot">check out this guide.</Link>
              </p>
            ),
          },
          // TODO: Enable once we have enough resources for community moderation
          // community: [
          //   {
          //     title: 'Zap not triggering - Facebook leads to Mailchimp',
          //     href:
          //       'https://community.zapier.com/discussion/452/zap-not-triggering-facebook-leads-to-mailchimp#latest',
          //     snippet:
          //       'Some apps have permissions or settings in their own software that cause complications in Zapier; in this discussion, a Zapier Expert guides a Zap creator through correcting this.',
          //   },
          //   {
          //     title: 'gmail to airtable',
          //     href:
          //       'https://community.zapier.com/discussion/195/things-not-to-do-on-google-sheets-that-are-involved-in-live-zaps#latest',
          //     snippet:
          //       'Some apps such as Gmail have restrictions on data movement for security reasons. A Zapier Expert explains one way to get around this.',
          //   },
          //   {
          //     title:
          //       'Things NOT to do on Google Sheets that are involved in live zaps',
          //     href:
          //       'https://community.zapier.com/discussion/195/things-not-to-do-on-google-sheets-that-are-involved-in-live-zaps#latest',
          //     snippet:
          //       'A set of tips from a Zapier Expert to avoid breaking Zaps while editing in Google Sheets, and to get the most out of Sheets on Zapier.',
          //   },
          // ],
          contact: createDefaultContact({
            media: {
              label: `Please provide a full-screen screenshot of the most recent record from your app that did not trigger.`,
              details: triggerHelp,
            },
          }),
        }),
      },
      {
        value: 'dynamic',
        label: `Data from the trigger isn't coming through`,
        zendeskCustomFieldValue: 'cf-missing-data',
        child: help({
          docs: {
            title: 'Check if the fields are mapped correctly in your action',
            body: () => (
              <Fragment>
                <p>This might happen for one of several reasons:</p>
                <ol>
                  <li>
                    Your action step may need to be mapped with fields. When you
                    map a field, what you're doing is selecting a particular
                    field from the trigger and saying that the value for this
                    field should go into this other field in your action step.
                    You can learn{' '}
                    <Link href="/help/create/basics/set-up-your-zap-action#customize-your-options">
                      how to do that here.
                    </Link>
                  </li>
                  <li>
                    If you're in your action step and you want to have a field
                    dynamically use information from an earlier step, but it's
                    only showing you fields from that action which would set it
                    to the same thing each time, you'll need to use custom
                    values. You can{' '}
                    <Link href="/help/create/customize/add-custom-values-to-dropdown-menu-fields-in-zaps">
                      learn more about it and watch a video tutorial on our
                      guide here.
                    </Link>
                  </li>
                </ol>
              </Fragment>
            ),
          },
          // TODO: Enable once we have enough resources for community moderation
          // community: [
          //   {
          //     title:
          //       'Need some help with a Jira Zap - creating a Jira "issue" type, from google sheets',
          //     href:
          //       'https://community.zapier.com/discussion/425/need-some-help-with-a-jira-zap-creating-a-jira-issue-type-from-google-sheets',
          //     snippet:
          //       'A user’s data isn’t passing from Google Sheets into Jira, so a community member suggests a way to double check the data is formatted correctly.',
          //   },
          //   {
          //     title:
          //       'Things NOT to do on Google Sheets that are involved in live zaps',
          //     href:
          //       'https://community.zapier.com/discussion/195/things-not-to-do-on-google-sheets-that-are-involved-in-live-zaps#latest',
          //     snippet:
          //       'A set of tips from a Zapier Expert to avoid breaking Zaps while editing in Google Sheets, and to get the most out of Sheets on Zapier.',
          //   },
          //   {
          //     title: 'Messenger to Gmail Not Pulling In Names',
          //     href:
          //       'https://community.zapier.com/discussion/535/messenger-to-gmail-not-pulling-in-names',
          //     snippet:
          //       'Some apps have privacy rules that cause Zaps to work in unexpected ways.',
          //   },
          // ],
          contact: createDefaultContact({
            media: {
              label: `Please provide a full-screen screenshot of the most recent item where the field existed in the trigger app, but wasn't in the Zap editor or action app.`,
              details: triggerHelp,
            },
          }),
        }),
      },
      {
        value: 'missing',
        label: 'Fields or options are missing',
        zendeskCustomFieldValue: 'cf-missing-fields',
        child: missingChild,
      },
      {
        value: 'samples',
        label: `My Zap isn't finding sample data`,
        zendeskCustomFieldValue: 'cf-missing-sample-data',
        child: help({
          docs: {
            title: 'Create a new record in your trigger app',
            body: () => (
              <Fragment>
                <p>This might happen for one of several reasons:</p>
                <ol>
                  <li>
                    Make sure you've recently created a record in the trigger
                    app for Zapier to find. You may even want to create one
                    fresh just as a test so Zapier can find it. This often helps
                    with issues on not finding sample data.
                  </li>
                  <li>
                    Make sure you wait the full 3 minutes for Zapier to find the
                    sample data. This can take a while at times.
                  </li>
                  <li>
                    You may want to try clicking the option to skip the test,
                    which often loads pre-made sample data from the app that you
                    can use to finish up your Zap.
                  </li>
                </ol>
              </Fragment>
            ),
          },
          // TODO: Enable once we have enough resources for community moderation
          // community: [
          //   {
          //     title: 'Issues finding Hubspot data sample…',
          //     href:
          //       'https://community.zapier.com/discussion/319/issues-finding-hubspot-data-sample',
          //     snippet:
          //       'Some apps require Zap creators to perform their trigger while Zapier is looking for sample data.',
          //   },
          //   {
          //     title: 'Quirk With Google Sheets Trigger Testing',
          //     href:
          //       'https://community.zapier.com/discussion/465/quirk-with-google-sheets-trigger-testing',
          //     snippet:
          //       'If a formula updates data in a Google Sheet, Zapier won’t be able to find that data to use in a test.',
          //   },
          //   {
          //     title: 'Dynamic account lookup with Zapier',
          //     href:
          //       'https://community.zapier.com/discussion/473/dynamic-account-lookup-with-zapier#latest',
          //     snippet:
          //       'A Zapier Expert explains how to pass data from one Zap to another using Google Sheets.',
          //   },
          // ],
          contact: createDefaultContact({
            media: {
              label: `Please provide a full-screen screenshot of the most recent item in your trigger, or the item you expected to get as a sample.`,
              details: `If there haven't been any new items from the last 7 days, create a new record that has the field filled out and test your Zap. For example, if your trigger is something like “New customer”, add a new customer as a test. Then, provide the record you created as a screenshot by linking or uploading it below. That way we can take a better look at where the problem might be occurring.`,
            },
          }),
        }),
      },
      {
        value: 'connecting',
        label: `I can't connect my app account to Zapier`,
        zendeskCustomFieldValue: 'cf-cant-connect-app',
        child: help({
          docs: {
            title: 'Check your app credentials and permissions',
            body: () => (
              <Fragment>
                <p>This might happen for one of several reasons:</p>
                <ol>
                  <li>
                    You may have entered your account credentials incorrectly.
                    Double check that you are correctly entering your password,
                    API keys, or any other field required when connecting.
                  </li>
                  <li>
                    Some apps have restrictions on which paying users can use a
                    3rd party like Zapier to connect to it. Look up the{' '}
                    <Link href="https://www.zapier.com/help">
                      help docs for your app
                    </Link>{' '}
                    to see if your app requires that you're on a certain plan to
                    be able to connect.
                  </li>
                  <li>
                    Similarly, some apps have restrictions on what level of user
                    can connect. For example, it may be that only an
                    administrators can connect. Look up the{' '}
                    <Link href="https://www.zapier.com/help">
                      help docs for your app
                    </Link>{' '}
                    to see if your app requires that you're a certain user level
                    to connect.
                  </li>
                </ol>
              </Fragment>
            ),
          },
          // TODO: Enable once we have enough resources for community moderation
          // community: [
          //   {
          //     title:
          //       'Does Zapier integration work with free Microsoft teams account?',
          //     href:
          //       'https://community.zapier.com/discussion/478/does-zapier-integration-work-with-free-microsoft-teams-account',
          //     snippet:
          //       'A Zap creator ran into an issue because the Microsoft integration doesn’t support free teams.',
          //   },
          //   {
          //     title: 'Zap not triggering - Facebook leads to Mailchimp',
          //     href:
          //       'https://community.zapier.com/discussion/452/zap-not-triggering-facebook-leads-to-mailchimp',
          //     snippet:
          //       'A Zap would test correctly but wouldn’t run until the Zap creator adjusted permissions in Facebook Lead Ads.',
          //   },
          //   {
          //     title: 'gmail to airtable',
          //     href:
          //       'https://community.zapier.com/discussion/446/gmail-to-airtable',
          //     snippet:
          //       'Gmail has security restrictions on integrations with some Apps. An Expert explains how to use Google Sheets to work around some restrictions.',
          //   },
          // ],
          contact: createDefaultContact({
            message: {
              label: `Please describe the issue you're seeing in detail, and provide information about your trigger app's account.`,
              details: `If relevant, let us know the following information about your trigger app's account: plan level, administrative rights, and/or version.`,
            },
            media: {
              label: `Please provide a full-screen screenshot of any error that appeared.`,
            },
            task: false,
          }),
        }),
      },
      {
        value: 'other',
        label: 'Other issues',
        zendeskCustomFieldValue: 'cf-other-troubleshooting',
        child: help({
          docs: {
            title: 'Check the help articles for your apps',
            body: () => (
              <p>
                For more help troubleshooting, check the{' '}
                <Link href="https://www.zapier.com/help">
                  help docs for the app that's experiencing an error
                </Link>{' '}
                to find potential answers and solutions.
              </p>
            ),
          },
          // TODO: Enable once we have enough resources for community moderation
          // community: [
          //   {
          //     title: 'Issues finding Hubspot data sample…',
          //     href:
          //       'https://community.zapier.com/discussion/319/issues-finding-hubspot-data-sample',
          //     snippet:
          //       'Some apps require Zap creators to perform their trigger while Zapier is looking for sample data.',
          //   },
          //   {
          //     title: 'How to find webhook amongst zaps',
          //     href:
          //       'https://community.zapier.com/discussion/531/how-to-find-webhook-amongst-zaps#latest',
          //     snippet:
          //       'A Zapier Expert shares some code to help a Zap creator find a webhook in a list of Zaps.',
          //   },
          // ],
          contact: createDefaultContact(),
        }),
      },
    ],
  }),
});

export default troubleshooting;
